/**
 * @file
 * Kit list with headings and show-more pagination, and its rows.
 */

((Drupal) => {
  const { berTags } = Drupal;
  const { components } = berTags;
  const { el } = berTags.dom;
  const { scrollToEnd } = berTags.util;
  const { Container } = window.BerUi;
  const ui = Drupal.berUI;
  const { base } = ui.compose;

  // The nearest ancestor that actually scrolls, so "show more" can reveal
  // the new batch without the caller having to know the popover's body.
  const scrollableAncestor = (node) => {
    let current = node.parentElement;
    while (current && current !== document.body) {
      const { overflowY } = getComputedStyle(current);
      if (
        (overflowY === 'auto' || overflowY === 'scroll') &&
        current.scrollHeight > current.clientHeight
      ) {
        return current;
      }
      current = current.parentElement;
    }
    return null;
  };

  // An option list: rows the caller renders, optional group headings, and
  // show-more pagination in the tags link style.
  ui.list = ({ classes = '', role = 'listbox', pagination = null } = {}) => {
    const root = el('div', `ber-ui__list ${classes}`.trim());
    root.setAttribute('role', role);
    let more = null;
    if (pagination) {
      more = el(
        'button',
        'ber-tags__more ber-tags__more--reveal ber-ui__list-more',
      );
      more.type = 'button';
      more.hidden = true;
      more.addEventListener('click', () => {
        // Scroll the list's scroll container to its end so the freshly
        // loaded batch is revealed. onMore may be async (a browse fetch),
        // so wait on its result before scrolling; pass scroll: false to
        // opt out, or scroller to override the auto-detected container.
        const outcome = pagination.onMore();
        if (pagination.scroll === false) {
          return;
        }
        Promise.resolve(outcome).then(() => {
          const target =
            (typeof pagination.scroller === 'function'
              ? pagination.scroller()
              : pagination.scroller) || scrollableAncestor(root);
          if (target) {
            scrollToEnd(target);
          }
        });
      });
    }
    const api = Object.assign(new Container(root), {
      more,
      clear() {
        root.textContent = '';
        return api;
      },
      addComponent(child) {
        root.appendChild(child.el || child);
        return api;
      },
      add(child) {
        return api.addComponent(child);
      },
      heading(text, modifier = '') {
        const node = el('div', `ber-ui__list-heading ${modifier}`.trim());
        node.textContent = text;
        root.appendChild(node);
        return api;
      },
      // remaining < 0 means unknown; 0 hides the button.
      setMore(remaining, next) {
        if (!more) {
          return api;
        }
        more.hidden = remaining === 0;
        if (remaining > 0) {
          more.textContent = Drupal.t(
            'Load @next more (@remaining remaining)',
            {
              '@next': Math.min(next || remaining, remaining),
              '@remaining': remaining,
            },
          );
        } else if (remaining < 0) {
          more.textContent = Drupal.t('Load more');
        }
        return api;
      },
    });
    ui.emit('list', { component: api });
    return api;
  };

  // One list row: label plus the subcomponent area - counter, drill arrow
  // into a folder parent - riding on the shared row markup.
  ui.listItem = ({ item = {}, meta = {}, onPick, onDrill } = {}) => {
    const row = components.listItem(item);
    const content = components.listItemContent(row, meta);
    if (onPick) {
      content.main.addEventListener('click', () => onPick(item));
    }
    if (onDrill) {
      content.arrow.classList.remove('is-hidden');
      content.arrow.addEventListener('click', (event) => {
        event.stopPropagation();
        onDrill(item);
      });
    }
    return Object.assign(base(row), {
      main: content.main,
      title: content.title,
      count: content.count,
      arrow: content.arrow,
      setCount(value) {
        content.count.textContent = String(value);
        content.count.classList.toggle('is-hidden', !value);
        return this;
      },
    });
  };

  ui.register('list', ui.list);
  ui.register('listItem', ui.listItem);
})(Drupal);
